import type { TypedSupabaseClient } from '../client';
import { ConfirmedMutationRejectedError } from '../../../interfaces/src';
import type {
  IStockRepository,
  Warehouse,
  CreateWarehouseInput,
  UpdateWarehouseInput,
  StockMovement,
  CreateStockMovementInput,
  ArticleStock,
  Inventory,
  InventoryLine,
  CreateInventoryInput,
  StockExitDocument,
  StockExitDocumentLine,
  CreateStockExitDocumentInput,
  StockVehicleDocumentType,
  StockTransferDocument,
  StockTransferLine,
  CreateStockTransferInput,
  ReceiveStockTransferInput,
  StockAtomicRequestKind,
} from '../../../interfaces/src';
import { ensureLowStockNotificationsForArticles } from './low-stock-notifications';
import { buildDocumentNumber } from '@erp/domain/numbering';
import {
  normalizeStockQuantity,
  normalizeInventoryCountLines,
  validateStockTransferDispatch,
  validateStockTransferDraft,
  validateStockTransferReceipt,
  buildVehicleStockEffects,
  resolveVehicleStockWarehouse,
  vehicleStockDocumentSchema,
  VehicleStockValidationError,
  type VehicleStockEffect,
} from '@erp/domain/inventory';
import { resolveDocNumberYear } from './doc-number-year';
import { chunkIds, fetchAllRows } from './fetch-all-rows';
import { SupabaseStoreRepository } from './store-repository';

const LEGACY_STOCK_DOC_META_PREFIX = '[ERP_STOCK_DOCUMENT_META:';
const LEGACY_STOCK_DOC_META_SUFFIX = ']';

function newStockEntityId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    },
  );
}

interface LegacyStockDocumentMeta {
  documentType?: StockVehicleDocumentType;
  parentDocumentId?: string | null;
}

function isStockVehicleDocumentType(
  value: unknown,
): value is StockVehicleDocumentType {
  return value === 'exit' || value === 'entry';
}

function parseLegacyStockDocumentNotes(notes: string | null | undefined): {
  meta: LegacyStockDocumentMeta;
  notes: string | null;
} {
  if (!notes) return { meta: {}, notes: notes ?? null };
  const start = notes.indexOf(LEGACY_STOCK_DOC_META_PREFIX);
  if (start === -1) return { meta: {}, notes };
  const bodyStart = start + LEGACY_STOCK_DOC_META_PREFIX.length;
  const end = notes.indexOf(LEGACY_STOCK_DOC_META_SUFFIX, bodyStart);
  if (end === -1) return { meta: {}, notes };

  const rawMeta = notes.slice(bodyStart, end);
  const cleanedNotes =
    `${notes.slice(0, start)}${notes.slice(end + LEGACY_STOCK_DOC_META_SUFFIX.length)}`.trim();
  try {
    const parsed = JSON.parse(rawMeta) as Record<string, unknown>;
    const documentType = isStockVehicleDocumentType(parsed['documentType'])
      ? parsed['documentType']
      : undefined;
    const parentDocumentId =
      typeof parsed['parentDocumentId'] === 'string'
        ? parsed['parentDocumentId']
        : null;
    return {
      meta: { documentType, parentDocumentId },
      notes: cleanedNotes || null,
    };
  } catch {
    return { meta: {}, notes };
  }
}

function withLegacyStockDocumentMeta(
  notes: string | undefined,
  meta: Required<Pick<LegacyStockDocumentMeta, 'documentType'>> &
    Pick<LegacyStockDocumentMeta, 'parentDocumentId'>,
): string {
  const marker = `${LEGACY_STOCK_DOC_META_PREFIX}${JSON.stringify(meta)}${LEGACY_STOCK_DOC_META_SUFFIX}`;
  const cleanNotes = notes?.trim();
  return cleanNotes ? `${marker}\n${cleanNotes}` : marker;
}

function isMissingSchemaColumnError(error: { message: string }): boolean {
  return (
    error.message.includes('Could not find the') &&
    error.message.includes('column')
  );
}

// ── Mappers ──────────────────────────────────────────────────────────

function mapWarehouse(row: Record<string, unknown>): Warehouse {
  return {
    id: row['id'] as string,
    storeId: row['store_id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    isDefault: (row['is_default'] as boolean) ?? false,
  };
}

function mapMovement(row: Record<string, unknown>): StockMovement {
  return {
    id: row['id'] as string,
    articleId: row['article_id'] as string,
    variantId: (row['variant_id'] as string) ?? null,
    warehouseId: row['warehouse_id'] as string,
    movementType: row['movement_type'] as StockMovement['movementType'],
    quantity: row['quantity'] as number,
    referenceType: (row['reference_type'] as string) ?? null,
    referenceId: (row['reference_id'] as string) ?? null,
    movementDate: row['movement_date'] as string,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
  };
}

function mapStockTransferLine(row: Record<string, unknown>): StockTransferLine {
  return {
    id: row['id'] as string,
    transferId: row['transfer_id'] as string,
    lineNumber: Number(row['line_number']),
    articleId: row['article_id'] as string,
    requestedQuantity: Number(row['requested_quantity']),
    receivedQuantity:
      row['received_quantity'] === null ||
      row['received_quantity'] === undefined
        ? null
        : Number(row['received_quantity']),
  };
}

function mapStockTransfer(row: Record<string, unknown>): StockTransferDocument {
  const lines = row['stock_transfer_lines'] as
    | Record<string, unknown>[]
    | undefined;
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    originWarehouseId: row['origin_warehouse_id'] as string,
    destinationWarehouseId: row['destination_warehouse_id'] as string,
    status: row['status'] as StockTransferDocument['status'],
    transferDate: row['transfer_date'] as string,
    notes: (row['notes'] as string) ?? null,
    responsibleUserId: (row['responsible_user_id'] as string) ?? null,
    customProperties:
      (row['custom_properties'] as StockTransferDocument['customProperties']) ??
      {},
    dispatchedAt: (row['dispatched_at'] as string) ?? null,
    receivedAt: (row['received_at'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines
      ?.map(mapStockTransferLine)
      .sort((a, b) => a.lineNumber - b.lineNumber),
  };
}

function mapInventory(row: Record<string, unknown>): Inventory {
  const lines = row['inventory_lines'] as Record<string, unknown>[] | undefined;
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    warehouseId: row['warehouse_id'] as string,
    inventoryDate: row['inventory_date'] as string,
    isValidated: (row['is_validated'] as boolean) ?? false,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    lines: lines ? lines.map(mapInventoryLine) : undefined,
  };
}

function mapInventoryLine(row: Record<string, unknown>): InventoryLine {
  return {
    id: row['id'] as string,
    inventoryId: row['inventory_id'] as string,
    articleId: row['article_id'] as string,
    theoreticalQty: row['theoretical_qty'] as number,
    countedQty: row['counted_qty'] as number,
    difference: row['difference'] as number,
  };
}

function mapStockExitDocument(row: Record<string, unknown>): StockExitDocument {
  const lines = row['stock_exit_document_lines'] as
    | Record<string, unknown>[]
    | undefined;
  const legacyNotes = parseLegacyStockDocumentNotes(
    (row['notes'] as string) ?? null,
  );
  const number = row['number'] as string;
  const rowDocumentType = row['document_type'];
  const documentType =
    legacyNotes.meta.documentType ??
    (isStockVehicleDocumentType(rowDocumentType)
      ? rowDocumentType
      : undefined) ??
    (number.startsWith('BE-') ? 'entry' : 'exit');
  return {
    id: row['id'] as string,
    storeId: row['store_id'] as string,
    warehouseId: (row['warehouse_id'] as string | null) ?? null,
    stockDeducted: row['stock_deducted'] === true,
    documentType,
    number,
    carRegistrationNumber: (row['car_registration_number'] as string | null) ?? '',
    exitDate: row['exit_date'] as string,
    parentDocumentId:
      legacyNotes.meta.parentDocumentId ??
      (row['parent_document_id'] as string) ??
      null,
    notes: legacyNotes.notes,
    customProperties:
      (row['custom_properties'] as StockExitDocument['customProperties']) ?? {},
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines ? lines.map(mapStockExitLine) : undefined,
  };
}

function mapStockExitLine(row: Record<string, unknown>): StockExitDocumentLine {
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: row['article_id'] as string,
    variantId: (row['variant_id'] as string) ?? null,
    variantLabel: (row['variant_label'] as string) ?? null,
    quantity: row['quantity'] as number,
    notes: (row['notes'] as string) ?? null,
  };
}

// ── Repository ───────────────────────────────────────────────────────

export class SupabaseStockRepository implements IStockRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private async notifyLowStock(articleIds: string[]): Promise<void> {
    try {
      await ensureLowStockNotificationsForArticles(this.supabase, articleIds);
    } catch (error) {
      console.warn('Low-stock notification failed:', error);
    }
  }

  async resolveAtomicRequest(
    clientRequestId: string,
    kind: StockAtomicRequestKind,
  ): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_stock_atomic_request' as never,
      { p_client_request_id: clientRequestId, p_request_kind: kind } as never,
    );
    if (error) throw error;
    return (data as string | null) ?? null;
  }

  // ── Warehouses ─────────────────────────────────────────────────

  async listWarehouses(storeId?: string): Promise<Warehouse[]> {
    const rows = await fetchAllRows((from, to) => {
      let query = this.supabase
        .from('authorized_warehouses' as 'warehouses')
        .select('*');
      if (storeId) query = query.eq('store_id', storeId);
      return query
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to);
    });
    return rows.map(mapWarehouse);
  }

  async createWarehouse(input: CreateWarehouseInput): Promise<Warehouse> {
    const storeId = input.storeId ?? (await this.resolveDefaultStoreId());
    const id = newStockEntityId();
    await this.mutateWarehouse('create', id, {
      store_id: storeId,
      code: input.code,
      name: input.name,
      is_default: input.isDefault ?? false,
    });
    return this.getAuthorizedWarehouseById(id);
  }

  async updateWarehouse(
    id: string,
    input: UpdateWarehouseInput,
  ): Promise<Warehouse> {
    const mapped: Record<string, unknown> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.isDefault !== undefined) mapped['is_default'] = input.isDefault;
    if (input.storeId !== undefined) {
      mapped['store_id'] = input.storeId;
    }

    await this.mutateWarehouse('update', id, mapped);
    return this.getAuthorizedWarehouseById(id);
  }

  async deleteWarehouse(id: string): Promise<void> {
    await this.mutateWarehouse('delete', id, {});
  }

  private async mutateWarehouse(
    action: 'create' | 'update' | 'delete',
    id: string,
    payload: Record<string, unknown>,
  ): Promise<void> {
    const { data, error } = (await this.supabase.rpc(
      'mutate_warehouse' as never,
      {
        p_action: action,
        p_warehouse_id: id,
        p_payload: payload,
      } as never,
    )) as { data: string | null; error: Error | null };
    if (error) throw error;
    if (data !== id) throw new Error('warehouse_mutation_failed');
  }

  private async resolveDefaultStoreId(): Promise<string> {
    const stores = await new SupabaseStoreRepository(this.supabase).list();
    const store = stores.find((candidate) => candidate.isDefault) ?? stores[0];
    if (!store) throw new Error('store_required');
    return store.id;
  }

  private async getAuthorizedWarehouseById(id: string): Promise<Warehouse> {
    const { data, error } = await this.supabase
      .from('authorized_warehouses' as 'warehouses')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return mapWarehouse(data);
  }

  private async warehouseIdsForStore(storeId: string): Promise<string[]> {
    const rows = await fetchAllRows<{ id: string }>((from, to) =>
      this.supabase
        .from('authorized_warehouses' as 'warehouses')
        .select('id')
        .eq('store_id', storeId)
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map((warehouse) => warehouse.id);
  }

  // ── Stock Movements ────────────────────────────────────────────

  async listMovements(
    articleId?: string,
    warehouseId?: string,
    storeId?: string,
  ): Promise<StockMovement[]> {
    const storeWarehouseIds =
      !warehouseId && storeId ? await this.warehouseIdsForStore(storeId) : null;
    if (storeWarehouseIds?.length === 0) return [];
    const rows = await fetchAllRows((from, to) => {
      let query = this.supabase
        .from('authorized_stock_movements' as 'stock_movements')
        .select('*');
      if (articleId) {
        query = query.eq('article_id', articleId);
      }
      if (warehouseId) {
        query = query.eq('warehouse_id', warehouseId);
      } else if (storeWarehouseIds) {
        query = query.in('warehouse_id', storeWarehouseIds);
      }
      return query
        .order('movement_date', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapMovement);
  }

  async listSalesDeliveryMovements(
    deliveryIds: string[],
  ): Promise<StockMovement[]> {
    const uniqueIds = [...new Set(deliveryIds.filter(Boolean))];
    if (uniqueIds.length === 0) return [];
    const pages = await Promise.all(
      chunkIds(uniqueIds).map((ids) =>
        fetchAllRows((from, to) =>
          this.supabase
            .from('authorized_stock_movements' as 'stock_movements')
            .select('*')
            .eq('reference_type', 'sales_delivery')
            .eq('movement_type', 'out')
            .in('reference_id', ids)
            .order('movement_date', { ascending: false })
            .order('id', { ascending: false })
            .range(from, to),
        ),
      ),
    );
    return pages.flat().map(mapMovement);
  }

  async createMovement(
    input: CreateStockMovementInput,
  ): Promise<StockMovement> {
    if (input.referenceType === 'purchase_reception') {
      // Un BR, ses lignes et son entrée de stock forment désormais une seule
      // mutation de `IPurchaseRepository`. Autoriser encore une écriture stock
      // isolée réintroduirait exactement les retries doubles et états partiels
      // que cette frontière atomique supprime.
      throw new Error('purchase_reception_stock_managed_by_document');
    }
    if (input.referenceType === 'sales_credit_note') {
      // Credit-note returns must validate their source allocations and write
      // the credit document plus ledger effect in the same atomic command.
      throw new Error('sales_credit_note_stock_managed_by_document');
    }
    const movementId = newStockEntityId();
    const payload = {
      id: movementId,
      article_id: input.articleId,
      variant_id: input.variantId ?? null,
      warehouse_id: input.warehouseId,
      movement_type: input.movementType,
      quantity: input.quantity,
      reference_type: input.referenceType,
      reference_id: input.referenceId,
      movement_date: input.movementDate,
      notes: input.notes,
    };
    const documentBound = input.referenceType === 'sales_delivery';
    const adjustmentBound = input.movementType === 'adjustment';
    let created: Record<string, unknown>;
    if (documentBound) {
      const { data, error } = (await this.supabase.rpc(
        'create_document_stock_movement' as never,
        {
          p_article_id: input.articleId,
          p_variant_id: input.variantId ?? null,
          p_warehouse_id: input.warehouseId,
          p_movement_type: input.movementType,
          p_quantity: input.quantity,
          p_reference_type: input.referenceType,
          p_reference_id: input.referenceId,
          p_movement_date: input.movementDate,
          p_notes: input.notes ?? null,
          p_source_returns:
            input.sourceReturns?.map((sourceReturn) => ({
              credit_note_line_id: sourceReturn.creditNoteLineId,
              variant_id: input.variantId ?? null,
              quantity: sourceReturn.quantity,
            })) ?? null,
        } as never,
      )) as {
        data: Record<string, unknown> | null;
        error: Error | null;
      };
      if (error) throw error;
      if (!data) throw new Error('stock_movement_not_created');
      created = data;
    } else if (adjustmentBound) {
      const { data, error } = (await this.supabase.rpc(
        'create_stock_adjustment_atomic' as never,
        {
          p_article_id: input.articleId,
          p_variant_id: input.variantId ?? null,
          p_warehouse_id: input.warehouseId,
          p_quantity: input.quantity,
          p_reference_type: input.referenceType ?? 'manual_adjustment',
          p_reference_id: input.referenceId ?? null,
          p_movement_date: input.movementDate,
          p_notes: input.notes ?? null,
          p_client_request_id: input.clientRequestId ?? newStockEntityId(),
        } as never,
      )) as {
        data: Record<string, unknown> | null;
        error: Error | null;
      };
      if (error) throw error;
      if (!data) throw new Error('stock_movement_not_created');
      created = data;
    } else {
      const { error } = await this.supabase
        .from('stock_movements')
        .insert(payload as never);
      if (error) throw error;
      const { data, error: readError } = await this.supabase
        .from('authorized_stock_movements' as 'stock_movements')
        .select('*')
        .eq('id', movementId)
        .single();
      if (readError) throw readError;
      created = data as Record<string, unknown>;
    }
    if (input.referenceType !== 'transfer') {
      await this.notifyLowStock([input.articleId]);
    }
    await this.refreshStorefrontStock([input.articleId]);
    return mapMovement(created);
  }

  // ── Transfer documents ────────────────────────────────────────

  async listTransfers(storeId?: string): Promise<StockTransferDocument[]> {
    const storeWarehouseIds = storeId
      ? await this.warehouseIdsForStore(storeId)
      : null;
    if (storeWarehouseIds?.length === 0) return [];
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = this.supabase
        .from(
          'authorized_stock_transfer_documents' as 'stock_transfer_documents',
        )
        .select('*');
      if (storeWarehouseIds) {
        const ids = storeWarehouseIds.join(',');
        query = query.or(
          `origin_warehouse_id.in.(${ids}),destination_warehouse_id.in.(${ids})`,
        );
      }
      return query
        .order('transfer_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    const ids = rows.map((row) => row['id'] as string);
    if (ids.length === 0) return [];
    const linePages = await Promise.all(
      chunkIds(ids).map((idChunk) =>
        fetchAllRows<Record<string, unknown>>((from, to) =>
          this.supabase
            .from('authorized_stock_transfer_lines' as 'stock_transfer_lines')
            .select('*')
            .in('transfer_id', idChunk)
            .order('transfer_id', { ascending: true })
            .order('line_number', { ascending: true })
            .order('id', { ascending: true })
            .range(from, to),
        ),
      ),
    );
    const lines = linePages.flat();
    const byTransfer = new Map<string, Record<string, unknown>[]>();
    for (const line of lines) {
      const bucket = byTransfer.get(line['transfer_id'] as string) ?? [];
      bucket.push(line);
      byTransfer.set(line['transfer_id'] as string, bucket);
    }
    return rows.map((row) =>
      mapStockTransfer({
        ...row,
        stock_transfer_lines: byTransfer.get(row['id'] as string) ?? [],
      }),
    );
  }

  async getTransferById(
    id: string,
    storeId?: string,
  ): Promise<StockTransferDocument | null> {
    const storeWarehouseIds = storeId
      ? await this.warehouseIdsForStore(storeId)
      : null;
    if (storeWarehouseIds?.length === 0) return null;
    let query = this.supabase
      .from('authorized_stock_transfer_documents' as 'stock_transfer_documents')
      .select('*')
      .eq('id', id);
    if (storeWarehouseIds) {
      const ids = storeWarehouseIds.join(',');
      query = query.or(
        `origin_warehouse_id.in.(${ids}),destination_warehouse_id.in.(${ids})`,
      );
    }
    const { data, error } = await query.single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    const { data: lines, error: linesError } = await this.supabase
      .from('authorized_stock_transfer_lines' as 'stock_transfer_lines')
      .select('*')
      .eq('transfer_id', id)
      .order('line_number');
    if (linesError) throw linesError;
    return mapStockTransfer({
      ...(data as unknown as Record<string, unknown>),
      stock_transfer_lines: lines ?? [],
    });
  }

  async createTransfer(
    input: CreateStockTransferInput,
  ): Promise<StockTransferDocument> {
    const lines = validateStockTransferDraft(input);
    const { data: transferId, error } = await this.supabase.rpc(
      'create_stock_transfer_atomic' as never,
      {
        p_origin_warehouse_id: input.originWarehouseId,
        p_destination_warehouse_id: input.destinationWarehouseId,
        p_transfer_date: input.transferDate,
        p_notes: input.notes ?? null,
        p_responsible_user_id: input.responsibleUserId ?? null,
        p_custom_properties: input.customProperties ?? {},
        p_lines: lines.map((line) => ({
          article_id: line.articleId,
          quantity: line.quantity,
        })),
        p_client_request_id: input.clientRequestId ?? null,
      } as never,
    );
    if (error) throw error;
    const created = await this.getTransferById(transferId as string);
    if (!created) throw new Error('stockTransfer.notFound');
    return created;
  }

  async deleteTransfer(id: string): Promise<void> {
    const transfer = await this.getTransferById(id);
    if (!transfer) throw new Error('stockTransfer.notFound');
    // Once dispatched, the document is the audit trail for immutable ledger
    // movements. Only an untouched draft can be removed safely.
    if (transfer.status !== 'draft') {
      throw new Error('stockTransfer.deleteDraftOnly');
    }

    const { data, error } = await this.supabase.rpc(
      'delete_stock_transfer_draft_atomic' as never,
      { p_transfer_id: id } as never,
    );
    if (error) throw error;
    if (data !== id) throw new Error('stockTransfer.deleteDraftOnly');
  }

  async dispatchTransfer(
    id: string,
    clientRequestId?: string,
  ): Promise<StockTransferDocument> {
    if (clientRequestId) {
      const resolvedId = await this.resolveAtomicRequest(
        clientRequestId,
        'transfer_dispatch',
      );
      if (resolvedId) {
        if (resolvedId !== id)
          throw new Error('stock_atomic_request_resource_conflict');
        const resolved = await this.getTransferById(id);
        if (!resolved) throw new Error('stockTransfer.notFound');
        return resolved;
      }
    }
    const transfer = await this.getTransferById(id);
    if (!transfer) throw new Error('stockTransfer.notFound');
    const stock = await this.getAllStock(transfer.originWarehouseId);
    validateStockTransferDispatch(transfer, stock);
    const lines = transfer.lines ?? [];
    const { error } = await this.supabase.rpc(
      'dispatch_stock_transfer_atomic' as never,
      {
        p_transfer_id: id,
        p_client_request_id: clientRequestId ?? null,
      } as never,
    );
    if (error) throw error;

    const articleIds = lines.map((line) => line.articleId);
    await this.notifyLowStock(articleIds);
    await this.refreshStorefrontStock(articleIds);
    const dispatched = await this.getTransferById(id);
    if (!dispatched || dispatched.status !== 'dispatched')
      throw new Error('stockTransfer.dispatchFailed');
    return dispatched;
  }

  async receiveTransfer(
    input: ReceiveStockTransferInput,
  ): Promise<StockTransferDocument> {
    if (input.clientRequestId) {
      const resolvedId = await this.resolveAtomicRequest(
        input.clientRequestId,
        'transfer_receive',
      );
      if (resolvedId) {
        if (resolvedId !== input.id)
          throw new Error('stock_atomic_request_resource_conflict');
        const resolved = await this.getTransferById(input.id);
        if (!resolved) throw new Error('stockTransfer.notFound');
        return resolved;
      }
    }
    const transfer = await this.getTransferById(input.id);
    if (!transfer) throw new Error('stockTransfer.notFound');
    const quantities = validateStockTransferReceipt(transfer, input.lines);
    const lines = transfer.lines ?? [];
    const receiptLines = lines
      .map((line) => ({
        line_id: line.id,
        received_quantity: quantities.get(line.id) ?? 0,
      }))
      .sort((left, right) => left.line_id.localeCompare(right.line_id));
    const { error } = await this.supabase.rpc(
      'receive_stock_transfer_atomic' as never,
      {
        p_transfer_id: input.id,
        p_lines: receiptLines,
        p_client_request_id: input.clientRequestId ?? null,
      } as never,
    );
    if (error) throw error;

    const articleIds = lines.map((line) => line.articleId);
    await this.refreshStorefrontStock(articleIds);
    const received = await this.getTransferById(input.id);
    if (!received || received.status !== 'received')
      throw new Error('stockTransfer.receiptFailed');
    return received;
  }

  // ── Stock Calculation ──────────────────────────────────────────

  async getArticleStock(
    articleId: string,
    warehouseId?: string,
    storeId?: string,
  ): Promise<ArticleStock[]> {
    const storeWarehouseIds =
      !warehouseId && storeId ? await this.warehouseIdsForStore(storeId) : null;
    if (storeWarehouseIds?.length === 0) return [];
    const rows = await fetchAllRows((from, to) => {
      let query = this.supabase
        .from('authorized_stock_movements' as 'stock_movements')
        .select('article_id, warehouse_id, movement_type, quantity')
        .eq('article_id', articleId);
      if (warehouseId) {
        query = query.eq('warehouse_id', warehouseId);
      } else if (storeWarehouseIds) {
        query = query.in('warehouse_id', storeWarehouseIds);
      }
      return query.order('id').range(from, to);
    });
    return aggregateStock(rows as unknown as Record<string, unknown>[]);
  }

  async getAllStock(
    warehouseId?: string,
    storeId?: string,
  ): Promise<ArticleStock[]> {
    const storeWarehouseIds =
      !warehouseId && storeId ? await this.warehouseIdsForStore(storeId) : null;
    if (storeWarehouseIds?.length === 0) return [];
    const rows = await fetchAllRows((from, to) => {
      let query = this.supabase
        .from('authorized_stock_movements' as 'stock_movements')
        .select('article_id, warehouse_id, movement_type, quantity');
      if (warehouseId) {
        query = query.eq('warehouse_id', warehouseId);
      } else if (storeWarehouseIds) {
        query = query.in('warehouse_id', storeWarehouseIds);
      }
      return query.order('id').range(from, to);
    });
    return aggregateStock(rows as unknown as Record<string, unknown>[]);
  }

  /**
   * Mirror an article's current total stock (across all warehouses) onto its
   * storefront_products row, if one exists. The public storefront can't read
   * stock_movements (anon), so it relies on this denormalized value to show
   * "out of stock". Best-effort: a sync failure must never roll back the
   * stock movement that triggered it.
   */
  private async refreshStorefrontStock(articleIds: string[]): Promise<void> {
    await mirrorStorefrontStockForArticles(this.supabase, articleIds);
  }

  // ── Inventories ────────────────────────────────────────────────

  async listInventories(
    warehouseId?: string,
    storeId?: string,
  ): Promise<Inventory[]> {
    const storeWarehouseIds =
      !warehouseId && storeId ? await this.warehouseIdsForStore(storeId) : null;
    if (storeWarehouseIds?.length === 0) return [];
    const rows = await fetchAllRows((from, to) => {
      let query = this.supabase
        .from('authorized_inventories' as 'inventories')
        .select('*');
      if (warehouseId) {
        query = query.eq('warehouse_id', warehouseId);
      } else if (storeWarehouseIds) {
        query = query.in('warehouse_id', storeWarehouseIds);
      }
      return query
        .order('inventory_date', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapInventory);
  }

  async getInventoryById(id: string): Promise<Inventory | null> {
    const { data, error } = await this.supabase
      .from('authorized_inventories' as 'inventories')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    const { data: lines, error: linesError } = await this.supabase
      .from('authorized_inventory_lines' as 'inventory_lines')
      .select('*')
      .eq('inventory_id', id);
    if (linesError) throw linesError;
    return mapInventory({
      ...(data as unknown as Record<string, unknown>),
      inventory_lines: lines ?? [],
    });
  }

  async createInventory(input: CreateInventoryInput): Promise<Inventory> {
    const lines = normalizeInventoryCountLines(input.lines);
    const { data: inventoryId, error } = await this.supabase.rpc(
      'create_inventory_atomic' as never,
      {
        p_warehouse_id: input.warehouseId,
        p_inventory_date: input.inventoryDate,
        p_notes: input.notes ?? null,
        p_lines: lines.map((line) => ({
          article_id: line.articleId,
          counted_quantity: line.countedQty,
        })),
        p_client_request_id: input.clientRequestId ?? null,
      } as never,
    );
    if (error) throw error;
    const created = await this.getInventoryById(inventoryId as string);
    if (!created) throw new Error('Inventory not found after creation');
    return created;
  }

  async validateInventory(
    id: string,
    clientRequestId?: string,
  ): Promise<Inventory> {
    if (clientRequestId) {
      const resolvedId = await this.resolveAtomicRequest(
        clientRequestId,
        'inventory_validate',
      );
      if (resolvedId) {
        if (resolvedId !== id)
          throw new Error('stock_atomic_request_resource_conflict');
        const resolved = await this.getInventoryById(id);
        if (!resolved) throw new Error('Inventory not found');
        return resolved;
      }
    }
    const inventory = await this.getInventoryById(id);
    if (!inventory) throw new Error('Inventory not found');
    if (inventory.isValidated) throw new Error('Inventory already validated');
    if (!inventory.lines || inventory.lines.length === 0) {
      throw new Error('Inventory has no lines');
    }

    const { error } = await this.supabase.rpc(
      'validate_inventory_atomic' as never,
      {
        p_inventory_id: id,
        p_client_request_id: clientRequestId ?? null,
      } as never,
    );
    if (error) throw error;
    const touchedArticles = inventory.lines
      .filter((line) => line.difference !== 0)
      .map((line) => line.articleId);
    if (touchedArticles.length > 0) {
      await this.notifyLowStock(touchedArticles);
      await this.refreshStorefrontStock(touchedArticles);
    }
    const validated = await this.getInventoryById(id);
    if (!validated?.isValidated) throw new Error('Inventory validation failed');
    return validated;
  }

  async listStockExitDocuments(
    documentType?: StockVehicleDocumentType,
  ): Promise<StockExitDocument[]> {
    // These views are introduced by the matching local migration and are not
    // in the last generated schema yet. Keep the escape isolated until types
    // can be regenerated against the migrated database.
    type PendingVehicleViewsClient = { from: (relation: string) => any };
    const pendingViews = this.supabase as unknown as PendingVehicleViewsClient;
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let query = pendingViews
        .from('authorized_stock_vehicle_documents')
        .select('*');
      if (documentType) query = query.eq('document_type', documentType);
      return query
        .order('exit_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return this.attachAuthorizedVehicleLines(rows);
  }

  async getStockExitDocumentById(
    id: string,
  ): Promise<StockExitDocument | null> {
    // Same generated-schema bridge as listStockExitDocuments above.
    type PendingVehicleViewsClient = { from: (relation: string) => any };
    const pendingViews = this.supabase as unknown as PendingVehicleViewsClient;
    const { data, error } = await pendingViews
      .from('authorized_stock_vehicle_documents')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    if (!data) return null;
    const [document] = await this.attachAuthorizedVehicleLines([
      data as unknown as Record<string, unknown>,
    ]);
    return document ?? null;
  }

  private async attachAuthorizedVehicleLines(
    rows: Record<string, unknown>[],
  ): Promise<StockExitDocument[]> {
    const ids = rows.map((row) => row['id'] as string).filter(Boolean);
    if (ids.length === 0) return [];
    // Same generated-schema bridge as the parent document query above.
    type PendingVehicleViewsClient = { from: (relation: string) => any };
    const pendingViews = this.supabase as unknown as PendingVehicleViewsClient;
    const pages = await Promise.all(
      chunkIds(ids).map((idChunk) =>
        fetchAllRows<Record<string, unknown>>((from, to) =>
          pendingViews
            .from('authorized_stock_vehicle_document_lines')
            .select('*')
            .in('document_id', idChunk)
            .order('document_id', { ascending: true })
            .order('line_number', { ascending: true })
            .order('id', { ascending: true })
            .range(from, to),
        ),
      ),
    );
    const byDocument = new Map<string, Record<string, unknown>[]>();
    for (const line of pages.flat()) {
      const documentId = line['document_id'] as string;
      const bucket = byDocument.get(documentId) ?? [];
      bucket.push(line);
      byDocument.set(documentId, bucket);
    }
    return rows.map((row) =>
      mapStockExitDocument({
        ...row,
        stock_exit_document_lines: byDocument.get(row['id'] as string) ?? [],
      }),
    );
  }

  async getStockVehiclePolicy(): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('get_stock_vehicle_policy');
    if (error) throw error;
    return data === true;
  }

  private async createStockVehicleWithEffects(
    input: CreateStockExitDocumentInput,
    source: StockExitDocument | null,
    previous: StockExitDocument | null = null,
  ): Promise<StockExitDocument> {
    const validation = vehicleStockDocumentSchema.safeParse(input);
    if (!validation.success) {
      const error = new Error('invalid_stock_target');
      if (!previous) throw new ConfirmedMutationRejectedError(error);
      throw error;
    }
    const parsed = validation.data;
    if (!parsed.clientRequestId) throw new Error('stock.vehicleRequestRequired');
    const documentType = parsed.documentType ?? 'exit';
    if (source && source.storeId !== parsed.storeId) {
      const error = new Error('invalid_stock_target');
      if (!previous) throw new ConfirmedMutationRejectedError(error);
      throw error;
    }
    if (parsed.warehouseId && previous && parsed.warehouseId !== previous.warehouseId) {
      throw new Error('stock_vehicle_request_payload_conflict');
    }
    if (parsed.warehouseId && source?.stockDeducted && parsed.warehouseId !== source.warehouseId) {
      throw new Error('invalid_stock_target');
    }
    let warehouseId: string | null;
    let effects: VehicleStockEffect[];
    try {
      warehouseId = documentType === 'entry'
        ? source?.warehouseId ?? null
        : previous?.warehouseId ?? resolveVehicleStockWarehouse(
          parsed.storeId!,
          await this.listWarehouses(parsed.storeId!),
          parsed.warehouseId ?? undefined,
        );
      effects = buildVehicleStockEffects({
        documentType,
        parentDocumentId: parsed.parentDocumentId,
        warehouseId,
        lines: parsed.lines,
        ...(source ? { source } : {}),
        // Concurrent return bounds are checked under the source row lock in
        // the atomic persistence command. Replanning against current returns
        // here would prevent recovery of our own successful timed-out request.
        documents: [],
      });
    } catch (error) {
      if (error instanceof VehicleStockValidationError && !previous) {
        throw new ConfirmedMutationRejectedError(error);
      }
      throw error;
    }
    const { data, error } = await this.supabase.rpc(
      'create_stock_vehicle_document_atomic',
      {
        p_client_request_id: parsed.clientRequestId,
        p_document: {
          store_id: parsed.storeId,
          document_type: documentType,
          warehouse_id: warehouseId,
          exit_date: parsed.exitDate,
          car_registration_number: parsed.carRegistrationNumber ?? '',
          parent_document_id: parsed.parentDocumentId ?? null,
          notes: parsed.notes ?? null,
          custom_properties: parsed.customProperties ?? {},
        },
        p_lines: parsed.lines.map((line) => ({
          article_id: line.articleId,
          variant_id: line.variantId ?? null,
          variant_label: line.variantLabel ?? null,
          quantity: line.quantity,
          notes: line.notes ?? null,
        })),
        p_effects: effects.map((effect) => ({
          article_id: effect.articleId,
          variant_id: effect.variantId ?? null,
          warehouse_id: effect.warehouseId,
          movement_type: effect.movementType,
          quantity: effect.quantity,
        })),
      },
    );
    if (error) {
      // Only a rejected transaction plus an absent durable receipt releases
      // the submitted form for edits. Timeouts and prior commits stay frozen.
      const sqlError = error as { code?: string; message?: string };
      if (['22023', '23514', '23503', '42501', '40001'].includes(sqlError.code ?? '')) {
        const receipt = await this.resolveStockVehicleRequest(parsed.clientRequestId);
        if (!receipt) throw new ConfirmedMutationRejectedError(error);
      }
      throw error;
    }
    if (typeof data !== 'string') throw new Error('stock_exit_create_returned_no_row');
    const created = await this.getStockExitDocumentById(data);
    if (!created) throw new Error('stock_exit_create_returned_no_row');
    if (created.stockDeducted) {
      // The ledger is already committed. Refresh the existing public mirror
      // without delaying acknowledgement or turning a saved BS into a failure.
      void this.refreshStorefrontStock([...new Set(parsed.lines.map((line) => line.articleId))]);
    }
    return created;
  }

  private async resolveStockVehicleRequest(clientRequestId: string): Promise<string | null> {
    const { data, error } = await this.supabase.rpc(
      'resolve_stock_vehicle_request',
      { p_client_request_id: clientRequestId },
    );
    if (error) throw error;
    return typeof data === 'string' ? data : null;
  }

  async createStockExitDocument(
    input: CreateStockExitDocumentInput,
  ): Promise<StockExitDocument> {
    if (!input.storeId) throw new Error('store_selection_required');
    // Creation is followed by an authorized read. Fail before allocating a
    // number or inserting anything when the deployed read schema is missing.
    // Never fall back to raw tables: that would bypass the read boundary.
    type VehicleReadProbe = {
      from: (relation: string) => {
        select: (columns: string) => {
          limit: (
            count: number,
          ) => PromiseLike<{ error: { message: string } | null }>;
        };
      };
    };
    const reads = this.supabase as unknown as VehicleReadProbe;
    for (const relation of [
      'authorized_stock_vehicle_documents',
      'authorized_stock_vehicle_document_lines',
    ]) {
      const { error } = await reads.from(relation).select(relation === 'authorized_stock_vehicle_document_lines' ? 'id,variant_id,variant_label' : 'id').limit(0);
      if (error) throw error;
    }
    const documentType = input.documentType ?? 'exit';
    const deductStock = await this.getStockVehiclePolicy();
    const previousId = input.clientRequestId
      ? await this.resolveStockVehicleRequest(input.clientRequestId)
      : null;
    const previous = previousId ? await this.getStockExitDocumentById(previousId) : null;
    if (previousId && !previous) throw new Error('vehicle_stock_request_resource_conflict');
    const source = documentType === 'entry' && input.parentDocumentId
      ? await this.getStockExitDocumentById(input.parentDocumentId)
      : null;
    // A persisted opted-in submission retains its atomic intent even when the
    // policy is disabled before retry. It must fail explicitly, never become a
    // stock-neutral legacy document. Non-opted-in callers do not supply a key.
    if (deductStock || source?.stockDeducted || input.clientRequestId) {
      return this.createStockVehicleWithEffects(input, source, previous);
    }
    const { data: seqValue, error: seqError } = await this.supabase.rpc(
      'get_next_sequence_value',
      {
        seq_name:
          documentType === 'entry' ? 'seq_stock_entry' : 'seq_stock_exit',
      },
    );
    if (seqError) throw seqError;
    const year = await resolveDocNumberYear(
      this.supabase,
      new Date(input.exitDate).getFullYear(),
    );
    const number = buildDocumentNumber(
      documentType === 'entry' ? 'BE' : 'BS',
      year,
      Number(seqValue),
    );

    type SbAny = { from: (table: string) => unknown };
    const sb = this.supabase as unknown as SbAny;
    const insertDocument = (row: Record<string, unknown>) =>
      (
        sb.from('stock_exit_documents') as {
          insert: (row: Record<string, unknown>) => {
            select: () => {
              single: () => Promise<{
                data: { id: string } | null;
                error: { message: string } | null;
              }>;
            };
          };
        }
      )
        .insert(row)
        .select()
        .single();

    let docRes = await insertDocument({
      store_id: input.storeId,
      document_type: documentType,
      number,
      car_registration_number: input.carRegistrationNumber ?? '',
      exit_date: input.exitDate,
      parent_document_id: input.parentDocumentId ?? null,
      notes: input.notes,
      custom_properties: input.customProperties ?? {},
    });
    if (docRes.error && isMissingSchemaColumnError(docRes.error)) {
      docRes = await insertDocument({
        store_id: input.storeId,
        number,
        car_registration_number: input.carRegistrationNumber ?? '',
        exit_date: input.exitDate,
        notes:
          documentType === 'entry'
            ? withLegacyStockDocumentMeta(input.notes, {
                documentType,
                parentDocumentId: input.parentDocumentId ?? null,
              })
            : input.notes,
      });
    }
    if (docRes.error) throw new Error(docRes.error.message);
    if (!docRes.data) throw new Error('stock_exit_create_returned_no_row');

    const lines = input.lines.map((line, idx) => ({
      document_id: docRes.data!.id,
      line_number: idx + 1,
      article_id: line.articleId,
      variant_id: line.variantId ?? null,
      variant_label: line.variantLabel ?? null,
      quantity: line.quantity,
      notes: line.notes,
    }));

    if (lines.length > 0) {
      const linesRes = await (
        sb.from('stock_exit_document_lines') as {
          insert: (
            rows: Record<string, unknown>[],
          ) => Promise<{ error: { message: string } | null }>;
        }
      ).insert(lines);
      if (linesRes.error) throw new Error(linesRes.error.message);
    }

    const created = await this.getStockExitDocumentById(docRes.data.id);
    if (!created)
      throw new Error('Stock exit document not found after creation');
    return created;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Aggregates stock movements client-side.
 * in → +quantity, out → -quantity, adjustment → quantity as-is (can be + or -)
 */
/**
 * Recompute an article's total stock (across warehouses) from the movement
 * ledger and mirror it onto its storefront_products row. Exported so the sales
 * and purchase repositories can refresh the mirror after they reverse a
 * document's stock movements on deletion. It is best-effort by default because
 * the ledger remains authoritative. Atomic, idempotently replayable mutations
 * may opt into propagation so the user is asked to retry until the public
 * mirror has also converged.
 */
export async function mirrorStorefrontStockForArticles(
  supabase: TypedSupabaseClient,
  articleIds: string[],
  options: { throwOnError?: boolean } = {},
): Promise<void> {
  const ids = [...new Set(articleIds.filter(Boolean))];
  if (ids.length === 0) return;
  try {
    // The normal authorized ledger is store-scoped. A restricted store user
    // must never receive tenant-wide movement rows just so the public stock
    // mirror can be refreshed. This RPC performs the aggregate server-side,
    // returns no stock data, and updates only the tenant's mirror rows.
    for (let offset = 0; offset < ids.length; offset += 500) {
      const { error } = await supabase.rpc('refresh_storefront_stock_mirror', {
        p_article_ids: ids.slice(offset, offset + 500),
      });
      if (error) throw error;
    }
  } catch (error) {
    if (options.throwOnError) throw error;
    // Best-effort mirror — keep the real stock ledger authoritative.
  }
}

function aggregateStock(rows: Record<string, unknown>[]): ArticleStock[] {
  const map = new Map<string, number>();

  for (const row of rows) {
    const articleId = row['article_id'] as string;
    const warehouseId = row['warehouse_id'] as string;
    const movementType = row['movement_type'] as string;
    const quantity = Number(row['quantity']);
    const key = `${articleId}::${warehouseId}`;

    const current = map.get(key) ?? 0;
    if (movementType === 'in') {
      map.set(key, normalizeStockQuantity(current + quantity));
    } else if (movementType === 'out') {
      map.set(key, normalizeStockQuantity(current - quantity));
    } else {
      // adjustment: quantity is the delta (positive or negative)
      map.set(key, normalizeStockQuantity(current + quantity));
    }
  }

  const result: ArticleStock[] = [];
  for (const [key, quantity] of map.entries()) {
    const [articleId, warehouseId] = key.split('::');
    result.push({ articleId: articleId!, warehouseId: warehouseId!, quantity });
  }
  return result;
}
